// src/pages/WaiterSalesPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { translateDynamic, getTransliteration, getProductName, getEntityTranslation } from '../../context/LanguageContext';
import tenantStorage from '../../services/tenantStorage';
import { format } from 'date-fns';
import Alert from '../../components/common/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SelectableCard from '../../components/common/SelectableCard';
import ChangeTableModal from '../../components/pos/modals/ChangeTableModal';
import { getCategoryIcon } from '../../constants/categoryIcons';

import api, { employeeService, orderTrackingService, modifierService } from '../../services/api';
import {
    deductStockForPendingOrder,
    deductStockForAddedItems,
    createOrderTracking,
    updateOrderTrackingRecords,
    calculateNewItemsForTracking,
    deductInventoryForAdditionalItems
} from '../../utils/OrderHelpers';
import useBusinessDay from '../../hooks/pos/useBusinessDay';
import useProductCatalog from '../../hooks/pos/useProductCatalog';
import useDiningSetup from '../../hooks/pos/useDiningSetup';
import usePendingOrders from '../../hooks/pos/usePendingOrders';
import useOrderBuilder from '../../hooks/pos/useOrderBuilder';
import usePromoEvaluator from '../../hooks/pos/usePromoEvaluator';

const WaiterSalesPage = () => {
    const { tenantConfig, getBrandName, getCurrency, username, employeeId, authType } = useAuth();
    const { t, language, translationDetails } = useLanguage();


    const [employeeName, setEmployeeName] = useState('');
    const [configLoaded, setConfigLoaded] = useState(false);

    // State management
    const [currentStep, setCurrentStep] = useState(1); // 1: table, 2: products, 3: confirm
    const {
        diningOptions, diningOptionObjects, availableTableNumbers,
        selectedDiningOption, setSelectedDiningOption,
        tableNumber, setTableNumber,
        loadDiningOptions,
    } = useDiningSetup(tenantConfig);
    // Products, categories, filtering handled by useProductCatalog
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // Mobile-specific state
    const [showProductModal, setShowProductModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productQuantity, setProductQuantity] = useState(1);
    const [productNotes, setProductNotes] = useState('');

    // Refs for mobile gestures
    const containerRef = useRef(null);

    const [initialMode, setInitialMode] = useState(null);

    // Get tenant-specific values
    const currency = getCurrency();

    const [occupiedTablesExpanded, setOccupiedTablesExpanded] = useState(false);
    const [originalProductsBeforeEdit, setOriginalProductsBeforeEdit] = useState([]);

    // Change table functionality
    const [changeTableModalOpen, setChangeTableModalOpen] = useState(false);
    const [orderToChangeTable, setOrderToChangeTable] = useState(null);
    const [newTableNumber, setNewTableNumber] = useState('');
    const [newDiningOption, setNewDiningOption] = useState('');
    const [editingOrderKey, setEditingOrderKey] = useState(null);

    const {
        isBusinessDay, setIsBusinessDay,
        businessDaySalesDate, setBusinessDaySalesDate,
    } = useBusinessDay();

    const {
        products, filteredProducts,
        categories, categoryObjects, categoryMap, categoryIcons, printableCategories,
        selectedCategory, setSelectedCategory,
        subcategories, selectedSubcategory, setSelectedSubcategory,
        bulkCalculatedStock, setBulkCalculatedStock,
        productSearch, setProductSearch,
        loadCatalog, refreshBulkStock,
    } = useProductCatalog(tenantConfig);

    const {
        pendingOrders, setPendingOrders,
        pendingOrderIds, setPendingOrderIds,
        printedOrders, setPrintedOrders,
        orderIdsRef,
        refreshTableOrders,
    } = usePendingOrders(isBusinessDay, businessDaySalesDate);

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

    const {
        selectedProducts, setSelectedProducts,
        notes, setNotes,
        addServiceCharge, setAddServiceCharge,
        addTax, setAddTax,
        selectedAddons, setSelectedAddons,
        currentProduct, setCurrentProduct,
        addonModalOpen, setAddonModalOpen,
        productAddons, setProductAddons,
        calculateTotal,
        addProductWithAddons,
        setModifierCatalog,
    } = useOrderBuilder({
        tenantConfig,
        selectedDiningOption,
        diningOptionObjects,
        bulkCalculatedStock,
        products,
        setError,
        t,
    });

    // Compute the subtotal for the promo engine (products only, no service charge)
    const waiterSubtotal = selectedProducts.reduce((sum, p) => {
        const base = (parseFloat(p.selling_price) || 0) * (parseInt(p.quantity) || 1);
        const addons = (p.add_ons || []).reduce((a, addon) =>
            a + (parseFloat(addon.price) || 0) * (parseInt(p.quantity) || 1), 0);
        return sum + base + addons;
    }, 0);

    // ── Promotion evaluation ─────────────────────────────────────────────────
    const {
        appliedPromo,
        allEligibleOptions: promoAllEligible,
        showPromoModal,
        setShowPromoModal,
        selectPromo,
        removePromo,
        resetPromo,
        promoDiscountAmount,
    } = usePromoEvaluator({
        selectedProducts,
        selectedDiningOption,
        subtotal: waiterSubtotal,
        enabled: !!tenantConfig,
    });

    // Helper function to get display name from username
    const getDisplayName = (username, employeeName) => {
        if (employeeName) return employeeName;

        // Convert employee_janedoe to Jane Doe
        if (username && username.startsWith('employee_')) {
            const name = username.replace('employee_', '');
            // Convert janedoe to Jane Doe (capitalize first letter and split at capital letters)
            return name.replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/^[a-z]/, (match) => match.toUpperCase())
                .replace(/([a-z])([a-z]*)/g, (match, first, rest) => first.toUpperCase() + rest);
        }

        return username || t('waiter');
    };

    // Helper function to check if a product is from the original order
    const isOriginalProduct = (product) => {
        if (!editingOrderKey || !originalProductsBeforeEdit) return false;

        return originalProductsBeforeEdit.some(originalProduct => {
            // Match by product code and add-ons combination
            if (originalProduct.product_code !== product.product_code) return false;

            const originalAddons = (originalProduct.add_ons || []).map(addon => addon.product_code).sort();
            const currentAddons = (product.add_ons || []).map(addon => addon.product_code).sort();

            if (originalAddons.length !== currentAddons.length) return false;
            return originalAddons.every((id, index) => id === currentAddons[index]);
        });
    };

    // Helper function to get original quantity for a product
    const getOriginalQuantity = (product) => {
        if (!editingOrderKey || !originalProductsBeforeEdit) return 0;

        const originalProduct = originalProductsBeforeEdit.find(originalProduct => {
            if (originalProduct.product_code !== product.product_code) return false;

            const originalAddons = (originalProduct.add_ons || []).map(addon => addon.product_code).sort();
            const currentAddons = (product.add_ons || []).map(addon => addon.product_code).sort();

            if (originalAddons.length !== currentAddons.length) return false;
            return originalAddons.every((id, index) => id === currentAddons[index]);
        });

        return originalProduct ? originalProduct.quantity : 0;
    };

    // Helper function to update product quantity with restrictions
    const updateProductQuantity = (productIndex, newQuantity) => {
        const product = selectedProducts[productIndex];
        const isOriginal = isOriginalProduct(product);
        const originalQty = getOriginalQuantity(product);

        // For original products, don't allow quantity to go below original amount
        if (isOriginal && newQuantity < originalQty) {
            setError(t('cannot_reduce_existing_items'));
            setTimeout(() => setError(''), 3000);
            return;
        }

        // Check stock limits
        let maxStock;
        const originalProduct = products.find(p => p.product_code === product.product_code);
        if (originalProduct.stock_calculation_type === 'auto') {
            const stockData = bulkCalculatedStock[originalProduct.product_code];
            maxStock = stockData ? stockData.available_stock + originalQty : originalQty; // Add back original quantity
        } else {
            maxStock = originalProduct.quantity === -9999 ? 999 : originalProduct.quantity + originalQty;
        }

        if (newQuantity > maxStock) {
            setError(t('only_x_available_in_stock', { count: maxStock - originalQty, product: product.name }));
            setTimeout(() => setError(''), 3000);
            return;
        }

        // Update quantity
        const updatedProducts = [...selectedProducts];
        updatedProducts[productIndex].quantity = newQuantity;
        setSelectedProducts(updatedProducts);
    };


    // Load actual employee name if logged in as employee
    useEffect(() => {
        const loadEmployeeName = async () => {
            if (authType === 'employee' && employeeId && !employeeId.startsWith('ACC_')) {
                try {
                    const employees = await employeeService.getAllEmployees();
                    const employee = employees.find(emp => emp.employee_id === employeeId);

                    if (employee) {
                        setEmployeeName(employee.name);
                    } else {
                        setEmployeeName(username || t('waiter'));
                    }
                } catch (error) {
                    console.error('Failed to load employee name:', error);
                    setEmployeeName(username || t('waiter'));
                }
            } else {
                setEmployeeName(username || t('waiter'));
            }
        };

        loadEmployeeName();
    }, [authType, employeeId, username]);

    // Load initial data
    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            try {
                setLoading(true);

                // Load dining options from API
                await loadDiningOptions({ tableOnly: true });

                if (!isMounted) return;

                // Load products, categories, and bulk stock via hook
                await loadCatalog();
                if (!isMounted) return;
                setConfigLoaded(true);

                // Keep the builder's modifierCatalog fresh too (Task 17), for
                // consistency with every other useOrderBuilder surface. NOTE:
                // this page has no <AddonSelectionModal> call site — it uses
                // its own legacy, hand-rolled inline addon UI built directly
                // against selectedAddons/setSelectedAddons (Task 7 finding),
                // so nothing here yet renders a modifier modal off this data.
                // Loading it keeps the hook's data correct in case a future
                // task rewires this page onto the shared modal.
                modifierService.getCatalog()
                    .then(setModifierCatalog)
                    .catch((err) => console.error('Failed to load modifier catalog:', err));

                // Load business day state
                try {
                    const businessDayState = await tenantStorage.getBusinessDayState();
                    setIsBusinessDay(businessDayState.isBusinessDay || false);
                    setBusinessDaySalesDate(businessDayState.salesDate || '');
                } catch (error) {
                    console.error('Error loading business day state:', error);
                    setIsBusinessDay(false);
                    setBusinessDaySalesDate('');
                }

                // Pending orders are loaded automatically by usePendingOrders hook
                setLoading(false);

            } catch (err) {
                console.error('Error in loadData:', err);
                if (isMounted) {
                    setError(t('failed_to_load_data'));
                    setLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, []);

    // Handle table selection
    const handleTableSelect = (tableNum) => {
        setTableNumber(tableNum.toString());
    };

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        setProductQuantity(1);
        setProductNotes('');
        setSelectedAddons([]); // Reset add-ons when opening modal
        setShowProductModal(true);
    };


    const addProductToOrder = () => {
        if (!selectedProduct) return;

        const selectedAddonIds = selectedAddons.sort(); // Sort for consistent comparison

        // Check if this exact product + addon combination already exists
        const existingProductIndex = selectedProducts.findIndex(product => {
            if (product.product_code !== selectedProduct.product_code) return false;

            // Compare add-ons
            const existingAddonIds = (product.add_ons || []).map(addon => addon.product_code).sort();

            // Check if addon arrays are the same
            if (existingAddonIds.length !== selectedAddonIds.length) return false;

            return existingAddonIds.every((id, index) => id === selectedAddonIds[index]);
        });

        if (existingProductIndex >= 0) {
            // Same product with same add-ons exists, increase quantity
            const updatedProducts = [...selectedProducts];
            updatedProducts[existingProductIndex].quantity += productQuantity;
            if (productNotes) {
                updatedProducts[existingProductIndex].notes = productNotes;
            }
            setSelectedProducts(updatedProducts);
        } else {
            // New product or different add-on combination, add as new entry
            // Create a more robust unique ID
            const addonSuffix = selectedAddonIds.length > 0 ? `-addons-${selectedAddonIds.join('-')}` : '-no-addons';
            const uniqueId = `${selectedProduct.product_code}${addonSuffix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const productWithAddons = {
                ...selectedProduct,
                quantity: productQuantity,
                notes: productNotes,
                add_ons: selectedAddons.map(addonCode => {
                    const addon = selectedProduct.available_addons.find(a => a.product_code === addonCode);
                    return addon;
                }).filter(Boolean), // Remove any undefined add-ons
                add_on_total: selectedAddons.reduce((total, addonCode) => {
                    const addon = selectedProduct.available_addons.find(a => a.product_code === addonCode);
                    return total + (addon ? parseFloat(addon.price) : 0);
                }, 0),
                uniqueId: uniqueId
            };

            setSelectedProducts(prev => [...prev, productWithAddons]);
        }

        setShowProductModal(false);
        setSelectedProduct(null);
        setProductQuantity(1);
        setProductNotes('');
        setSelectedAddons([]);
    };

    // Remove product from order
    const removeProductFromOrder = (productId) => {
        const hasUniqueIdMatch = selectedProducts.some(p => p.uniqueId && p.uniqueId === productId);
        setSelectedProducts(prev => prev.filter(p => {
            const isMatch = hasUniqueIdMatch
                ? (p.uniqueId === productId)
                : (p.product_code === productId || p.id === productId);
            return !isMatch;
        }));
    };

    // Create order tracking records without printing
    const createOrderTrackingRecords = async (order) => {
        try {
            setSuccess(t('sending_order_to_kitchen_bar'));

            const trackingRecords = []; // Store tracking records created

            // Create tracking records for each printable category that has items
            for (const category of printableCategories) {
                const categoryItems = order.products.filter(p => p.category === category);
                if (categoryItems.length > 0) {
                    try {
                        const trackingData = {
                            transaction_id: null,
                            order_id: order.order_id,
                            category: category,
                            printed_categories: false,
                            items: categoryItems.map(item => ({
                                name: item.name,
                                quantity: item.quantity,
                                notes: item.notes || null,
                                add_ons: item.add_ons || []
                            })),
                            table_info: {
                                dining_option: selectedDiningOption,
                                table_number: tableNumber,
                                staff_name: getDisplayName(username, employeeName) || 'Unknown'
                            },
                            total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                            notes: `Staff: ${getDisplayName(username, employeeName)}${notes ? ` | ${notes}` : ''}` // Use helper function
                        };
                        const trackingResult = await orderTrackingService.createOrderTracking(trackingData);
                        trackingRecords.push(trackingResult);
                        // Dispatch cross-tab event
                        const eventData = {
                            type: 'orderSubmitted',
                            category: category,
                            orderType: 'new_order',
                            orderId: order.order_id,
                            timestamp: Date.now()
                        };

                        localStorage.setItem('orderEvent', JSON.stringify(eventData));
                        localStorage.removeItem('orderEvent');
                    } catch (trackingError) {
                        console.error(`Failed to create tracking for ${category}:`, trackingError);
                    }
                }
            }

            // Handle items not in printable categories - CREATE TRACKING RECORDS TOO
            const allCategories = [...new Set(order.products.map(p => p.category))];
            const nonPrintableCategories = allCategories.filter(cat => !printableCategories.includes(cat));

            for (const category of nonPrintableCategories) {
                const categoryItems = order.products.filter(p => p.category === category);
                if (categoryItems.length > 0) {
                    try {
                        const trackingData = {
                            transaction_id: null,
                            order_id: order.order_id,
                            category: category,
                            printed_categories: false,
                            items: categoryItems.map(item => ({
                                name: item.name,
                                quantity: item.quantity,
                                notes: item.notes || null,
                                add_ons: item.add_ons || []
                            })),
                            table_info: {
                                dining_option: selectedDiningOption,
                                table_number: tableNumber,
                                staff_name: getDisplayName(username, employeeName) || 'Unknown'
                            },
                            total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                            notes: `Staff: ${getDisplayName(username, employeeName)}${notes ? ` | ${notes}` : ''}` // Use helper function
                        };
                        const trackingResult = await orderTrackingService.createOrderTracking(trackingData);
                        trackingRecords.push(trackingResult);
                    } catch (trackingError) {
                        console.error(`Failed to create tracking for non-printable ${category}:`, trackingError);
                    }
                }
            }

            // Log tracking summary
            if (trackingRecords.length > 0) {
                setSuccess(t('order_sent_to_stations_ready_for_receipts', { count: trackingRecords.length }));
            }

        } catch (err) {
            console.error('Error during order tracking creation:', err);
            setError(t('error_processing_order_try_again'));
        }
    };

    // Edit order functionality
    const startEditOrder = (orderKey) => {
        const order = pendingOrders[orderKey];
        if (!order) return;

        // PRESERVE ORIGINAL PRODUCTS STATE BEFORE EDITING
        const originalProducts = JSON.parse(JSON.stringify(order.products || [])); // Deep copy
        setOriginalProductsBeforeEdit(originalProducts);

        // Set form data from order
        setSelectedDiningOption(order.diningOption);
        setTableNumber(order.tableNumber);
        setSelectedProducts(order.products || []);
        setNotes(order.notes || '');

        // Set edit mode
        setEditingOrderKey(orderKey);
        setCurrentStep(2); // Go to products step
    };


    const handleChangeTable = (orderKey) => {
        setOrderToChangeTable(orderKey);
        setChangeTableModalOpen(true);
        const order = pendingOrders[orderKey];
        if (order) {
            setNewDiningOption(order.diningOption);
            setNewTableNumber('');
        }
    };

    const saveEditedOrder = async () => {
        // Step 0: Check if business day has started
        if (!isBusinessDay || !businessDaySalesDate) {
            setError(t('order_id_not_found_refresh_page'));
            return;
        }

        if (!editingOrderKey) {
            return;
        }

        try {
            setLoading(true);

            // Try to get orderId from state first, then from ref
            let orderId = pendingOrderIds[editingOrderKey] || orderIdsRef.current[editingOrderKey];

            if (!orderId) {
                console.error('No order ID found for:', editingOrderKey);
                setError(t('order_id_not_found_refresh_page'));
                setLoading(false);
                return;
            }

            // Get the original order for comparison
            const originalOrder = pendingOrders[editingOrderKey];

            const updatedOrder = {
                ...pendingOrders[editingOrderKey],
                products: selectedProducts,
                notes: notes,
                subtotal: calculateTotal().subtotal,
                total: Math.max(0, calculateTotal().total - promoDiscountAmount),
                time: format(new Date(), 'HH:mm:ss'),
                appliedPromoId: appliedPromo?.promotion?.promotion_id || null,
                promoDiscountAmount: promoDiscountAmount,
            };

            const currentSalesDate = await tenantStorage.getState('salesDate');
            await tenantStorage.saveOrder(editingOrderKey, updatedOrder, false, true, orderId, currentSalesDate);

            try {
                // Update order tracking records
                await updateOrderTrackingRecords(
                    originalProductsBeforeEdit,
                    selectedProducts,
                    orderId,
                    originalOrder,
                    tenantConfig,
                    selectedDiningOption,
                    editingOrderKey,
                    employeeName || username,
                    products,
                    false,
                    { printableCategories }
                );
            } catch (error) {
                console.error('Error in updateOrderTrackingRecords:', error);
            }

            // The items this edit ADDED have to come off the shelf here —
            // see the same call in pos-app/surfaces/waiter/lib/drawerFlow.js.
            try {
                await deductStockForAddedItems(
                    originalProductsBeforeEdit, selectedProducts,
                    selectedDiningOption, tableNumber, username,
                );
            } catch (error) {
                console.error('deductStockForAddedItems failed:', error);
            }

            // Emit cross-tab events for updated categories
            const affectedCategories = [...new Set(selectedProducts.map(p => p.category))];

            affectedCategories.forEach(category => {
                const eventData = {
                    type: 'orderSubmitted',
                    category: category,
                    orderType: 'order_updated',
                    orderId: orderId,
                    timestamp: Date.now()
                };
                localStorage.setItem('orderEvent', JSON.stringify(eventData));
                localStorage.removeItem('orderEvent');
            });

            // Update local state
            setPendingOrders(prev => ({
                ...prev,
                [editingOrderKey]: updatedOrder
            }));

            // Update ref as well
            orderIdsRef.current[editingOrderKey] = orderId;

            // Reset form
            setSelectedProducts([]);
            setNotes('');
            setEditingOrderKey(null);
            setOriginalProductsBeforeEdit([]);
            setCurrentStep(1);
            setSelectedDiningOption(null);
            setTableNumber('');
            resetPromo();

            setSuccess(t('order_updated_successfully_sent_to_kitchen'));
            setTimeout(() => setSuccess(''), 3000);
            setLoading(false);

        } catch (error) {
            console.error('Error in saveEditedOrder:', error);
            setError(t('failed_to_update_order', { error: error.message }));
            setLoading(false);
        }
    };

    const processTableChange = async () => {
        if (!orderToChangeTable || !newTableNumber || !newDiningOption) {
            return;
        }

        try {
            setLoading(true);

            const newTableKey = `${newDiningOption}-${newTableNumber}`;

            // Check if new table is occupied
            if (pendingOrders[newTableKey]) {
                setError(t('table_already_occupied_specific', { option: newDiningOption, number: newTableNumber }));
                setLoading(false);
                return;
            }

            // Try to get orderId from state first, then from ref
            let orderId = pendingOrderIds[orderToChangeTable] || orderIdsRef.current[orderToChangeTable];

            if (!orderId) {
                console.error('No order ID found for:', orderToChangeTable);
                setError(t('order_id_not_found_refresh'));
                setLoading(false);
                return;
            }

            await tenantStorage.changeOrderTable(orderId, newTableKey, newDiningOption, newTableNumber);

            // Update local state
            const order = pendingOrders[orderToChangeTable];
            const updatedOrder = {
                ...order,
                diningOption: newDiningOption,
                tableNumber: newTableNumber
            };

            setPendingOrders(prev => {
                const newOrders = { ...prev };
                delete newOrders[orderToChangeTable];
                newOrders[newTableKey] = updatedOrder;
                return newOrders;
            });

            setPendingOrderIds(prev => {
                const newIds = { ...prev };
                delete newIds[orderToChangeTable];
                newIds[newTableKey] = orderId;
                return newIds;
            });

            // Update ref as well
            delete orderIdsRef.current[orderToChangeTable];
            orderIdsRef.current[newTableKey] = orderId;

            // Close modal and reset state
            setChangeTableModalOpen(false);
            setOrderToChangeTable(null);
            setNewTableNumber('');
            setNewDiningOption('');

            setSuccess(t('order_moved_to_table', { option: newDiningOption, number: newTableNumber }));
            setTimeout(() => setSuccess(''), 3000);
            setLoading(false);

        } catch (error) {
            console.error('Error in processTableChange:', error);
            setError(t('failed_to_change_table', { error: error.message }));
            setLoading(false);
        }
    };

    const submitOrder = async () => {
        // Step 0: Check if business day has started
        if (!isBusinessDay || !businessDaySalesDate) {
            setError(t('no_current_business_day_consult_manager'));            return;
        }

        if (!selectedDiningOption || !tableNumber || selectedProducts.length === 0) {
            setError(t('please_select_table_and_add_products'));
            return;
        }

        try {
            setLoading(true);

            const tableKey = `${selectedDiningOption}-${tableNumber}`;

            // Check if table is already occupied
            if (pendingOrders[tableKey]) {
                setError(t('table_already_occupied'));
                setLoading(false);
                return;
            }

            // 1. DEDUCT STOCK IMMEDIATELY when creating pending order
            try {
                await deductStockForPendingOrder(selectedProducts, selectedDiningOption, tableNumber, username);
            } catch (error) {
                console.error('Error deducting stock for pending order:', error);
                setError(t('failed_to_deduct_stock_try_again'));
                setLoading(false);
                return;
            }

            // 2. Create pending order
            const _wTotals = calculateTotal();
            const pendingOrder = {
                time: format(new Date(), 'HH:mm:ss'),
                date: format(new Date(), 'yyyy-MM-dd'),
                sales_date: await tenantStorage.getState('salesDate'),
                cashier: getDisplayName(username, employeeName),
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: [...selectedProducts],
                subtotal: _wTotals.subtotal,
                discount: 0,
                serviceCharge: _wTotals.serviceCharge,
                total: Math.max(0, _wTotals.total - promoDiscountAmount),
                notes: notes,
                paymentMethod: '',
                addServiceCharge: addServiceCharge,
                addTax: addTax,
                tax: _wTotals.tax,
                is_submitted: false,
                is_printed: false,
                // Promotion fields
                appliedPromoId: appliedPromo?.promotion?.promotion_id || null,
                promoDiscountAmount: promoDiscountAmount,
            };

            // 3. Save order and get order ID
            const currentSalesDate = await tenantStorage.getState('salesDate');
            const response = await tenantStorage.saveOrder(tableKey, pendingOrder, false, false, null, currentSalesDate);

            const orderId = response.order_id;
            if (!orderId) {
                throw new Error(t('failed_to_get_order_id'));
            }

            // 4. Create order tracking records
            const orderWithId = { ...pendingOrder, order_id: orderId };
            await createOrderTrackingRecords(orderWithId);

            // Emit events to notify OrderViewPage about new order
            const orderCategories = [...new Set(selectedProducts.map(p => p.category))];
            orderCategories.forEach(category => {
                const eventData = {
                    type: 'orderSubmitted',
                    category: category,
                    orderType: 'new_order',
                    orderId: orderId,
                    timestamp: Date.now()
                };

                // Cross-tab event (localStorage)
                localStorage.setItem('orderEvent', JSON.stringify(eventData));
                localStorage.removeItem('orderEvent');

                // Same-tab event (custom DOM event)
                window.dispatchEvent(new CustomEvent('orderEvent', { detail: eventData }));
            });

            // 5. Update local state with new order
            setPendingOrders(prev => ({
                ...prev,
                [tableKey]: { ...pendingOrder, order_id: orderId }
            }));

            setPendingOrderIds(prev => ({
                ...prev,
                [tableKey]: orderId
            }));

            // Update ref as well
            orderIdsRef.current[tableKey] = orderId;

            // 6. Refresh stock calculations
            await refreshBulkStock();

            // 7. Reset form
            setSelectedDiningOption(null);
            setTableNumber('');
            setSelectedProducts([]);
            setNotes('');
            setCurrentStep(1);
            setSelectedCategory(null);
            resetPromo();

            setSuccess(t('order_submitted_successfully_sent_to_kitchen'));
            setTimeout(() => setSuccess(''), 3000);
            setLoading(false);

        } catch (err) {
            console.error('Error submitting order:', err);
            setError(t('failed_to_submit_order'));
            setLoading(false);
        }
    };
    // Back navigation
    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        } else if (currentStep === 1 && initialMode) {
            setInitialMode(null);
            setSelectedDiningOption(null);
            setTableNumber('');
            setSelectedProducts([]);
            setNotes('');
            setEditingOrderKey(null);
        }
    };


    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className={`min-h-screen bg-gray-50 ${occupiedTablesExpanded ? 'waiter-sales-with-panel' : ''}`} ref={containerRef}>
            {/* Mobile Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center">
                        {(currentStep > 1 || (currentStep === 1 && initialMode)) && (
                            <button
                                onClick={handleBack}
                                className="mr-3 p-2 rounded-full hover:bg-gray-100"
                            >
                                ←
                            </button>
                        )}
                        <h1 className="text-lg font-semibold text-gray-900">
                            {currentStep === 1 ? t('select_table') :
                                currentStep === 2 ? t('add_products') : t('confirm_order')}
                        </h1>
                    </div>
                    <div className="text-sm text-gray-500">
                        {t('step_x_of_y', { current: currentStep, total: 3 })}
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white border-b">
                <div className="h-1 bg-gray-200">
                    <div
                        className="h-1 bg-primary-600 transition-all duration-300"
                        style={{ width: `${(currentStep / 3) * 100}%` }}
                    />
                </div>
            </div>

            {/* Business Day Status Alert - Enhanced Version */}
            {!isBusinessDay ? (
                <div className="px-4 py-2">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-red-800 font-medium">
                                    ⚠️ {t('no_active_business_day')}
                                </p>
                                <p className="text-red-700 text-sm mt-1">
                                    {t('consult_manager_to_start_business_day')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="px-4 py-2">
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-green-800 font-medium">
                                    ✓ {t('active_business_day')}
                                </p>
                                <p className="text-green-700 text-sm">
                                    {t('sales_date')}: {businessDaySalesDate}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Alerts */}
            {error && (
                <div className="px-4 py-2">
                    <Alert type="error" message={error} onClose={() => setError('')} />
                </div>
            )}
            {success && (
                <div className="px-4 py-2">
                    <Alert type="success" message={success} onClose={() => setSuccess('')} />
                </div>
            )}

            {/* Main Content - Only show if business day is active */}
            {!isBusinessDay ? (
                <div className="p-4">
                    <div className="bg-gray-100 border border-gray-300 rounded-lg p-8 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="text-gray-400 mb-4">
                                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {t('waiter_interface_locked')}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {t('start_business_day_to_access_waiter_features')}
                            </p>
                            <p className="text-sm text-gray-500">
                                {t('contact_manager_to_start_business_day')}
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                        {/* Step 0: Mode Selection (when no mode is selected) */}
                        {currentStep === 1 && !initialMode && (
                            <div className="p-4 space-y-6">
                                <div>
                                    <h2 className="text-lg font-medium text-gray-900 mb-6 text-center">
                                        {t('choose_action')}
                                    </h2>
                                    <div className="space-y-4">
                                        <button
                                            onClick={async () => {
                                                await refreshTableOrders();
                                                setInitialMode('new');
                                            }}
                                            className="w-full bg-primary-600 text-white py-4 rounded-lg font-medium text-lg hover:bg-primary-700 transition-colors"
                                        >
                                            {t('new_order')}
                                        </button>
                                        <button
                                            onClick={async () => {
                                                await refreshTableOrders();
                                                setInitialMode('edit');
                                            }}
                                            className="w-full bg-blue-600 text-white py-4 rounded-lg font-medium text-lg hover:bg-blue-700 transition-colors"
                                            disabled={Object.keys(pendingOrders).length === 0}
                                        >
                                            {t('edit_order')}
                                            {Object.keys(pendingOrders).length > 0 && (
                                                <span className="ml-2 bg-blue-800 px-2 py-1 rounded-full text-sm">
                                                    {Object.keys(pendingOrders).length}
                                                </span>
                                            )}
                                        </button>
                                        {Object.keys(pendingOrders).length === 0 && (
                                            <p className="text-center text-gray-500 text-sm">
                                                {t('no_orders_available_to_edit')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 1: Table Selection */}
                        {currentStep === 1 && initialMode === 'new' && (
                            <div className="p-4 space-y-6">
                                {/* Dining Options */}
                                <div>
                                    <h2 className="text-lg font-medium text-gray-900 mb-4">
                                        {t('select_dining_area')}
                                    </h2>
                                    <div className="grid grid-cols-1 gap-3">
                                        {diningOptions.map(option => (
                                            <div key={option}>
                                                <SelectableCard
                                                    label={
                                                        <div className="flex justify-between items-center w-full">
                                                            <span>{getEntityTranslation(diningOptionObjects[option], language, translationDetails) || translateDynamic(option, t)}</span>
                                                            {availableTableNumbers[option] && (
                                                                <span className="text-sm text-gray-500">
                                                                    {selectedDiningOption === option ? '▼' : '▶'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    }
                                                    selected={selectedDiningOption === option}
                                                    onClick={async () => {

                                                        await refreshTableOrders();
                                                        // Toggle the dining option
                                                        if (selectedDiningOption === option) {
                                                            setSelectedDiningOption(null);
                                                            setTableNumber('');
                                                        } else {
                                                            setSelectedDiningOption(option);
                                                            setTableNumber('');
                                                        }
                                                    }}
                                                />

                                                {/* Table Selection - Show only when this dining option is selected */}
                                                {selectedDiningOption === option && availableTableNumbers[option] && (
                                                    <div className="mt-3 ml-4 pl-4 border-l-2 border-primary-200 table-selection-area">
                                                        <h3 className="text-md font-medium text-gray-700 mb-3">
                                                            {t('select_option_table', { option })}
                                                        </h3>
                                                        <div className="grid grid-cols-3 gap-2">
                                                            {availableTableNumbers[option].map(tableNum => {
                                                                const tableKey = `${option}-${tableNum}`;
                                                                const isOccupied = pendingOrders[tableKey];

                                                                return (
                                                                    <SelectableCard
                                                                        key={tableNum}
                                                                        label={
                                                                            <div className="text-center">
                                                                                <div className="font-medium text-sm">
                                                                                    {typeof tableNum === 'string' ? tableNum : t('table_number', { number: tableNum })}
                                                                                </div>
                                                                                {isOccupied && (
                                                                                    <div className="text-xs text-red-600 mt-1">
                                                                                        {t('occupied')}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        }
                                                                        selected={tableNumber === tableNum.toString()}
                                                                        onClick={() => {
                                                                            if (!isOccupied) {
                                                                                if (tableNumber === tableNum.toString()) {
                                                                                    setTableNumber('');
                                                                                } else {
                                                                                    handleTableSelect(tableNum);
                                                                                }
                                                                            }
                                                                        }}
                                                                        disabled={isOccupied}
                                                                        customStyle={isOccupied ? {
                                                                            backgroundColor: '#fee2e2',
                                                                            borderColor: '#fca5a5',
                                                                            color: '#dc2626'
                                                                        } : null}
                                                                        className="dining-option-card"
                                                                    />
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Continue Button */}
                                                        {tableNumber && (
                                                            <div className="mt-4">
                                                                <button
                                                                    onClick={() => setCurrentStep(2)}
                                                                    className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium text-sm hover:bg-primary-700 transition-colors"
                                                                >
                                                                    {t('continue_with_table', { option: translateDynamic(option, t), number: tableNumber })} →                                                    </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 1 && initialMode === 'edit' && (
                            <div className="p-4 space-y-6">
                                <div>
                                    <h2 className="text-lg font-medium text-gray-900 mb-4">
                                        {t('select_order_to_edit')}
                                    </h2>
                                    <div className="space-y-3">
                                        {Object.entries(pendingOrders)
                                            .sort(([keyA], [keyB]) => {
                                                // Same sorting logic as in the occupied tables panel
                                                const parseOrderKey = (key) => {
                                                    const parts = key.split('-');
                                                    const tableNumber = parts[parts.length - 1];
                                                    const diningOption = parts.slice(0, -1).join('-');
                                                    const numericTable = isNaN(tableNumber) ? tableNumber : parseInt(tableNumber);
                                                    return { diningOption, tableNumber: numericTable };
                                                };
                                                const orderA = parseOrderKey(keyA);
                                                const orderB = parseOrderKey(keyB);
                                                const diningComparison = orderA.diningOption.localeCompare(orderB.diningOption);
                                                if (diningComparison !== 0) return diningComparison;
                                                if (typeof orderA.tableNumber === 'number' && typeof orderB.tableNumber === 'number') {
                                                    return orderA.tableNumber - orderB.tableNumber;
                                                }
                                                return String(orderA.tableNumber).localeCompare(String(orderB.tableNumber));
                                            })
                                            .map(([orderKey, order]) => (
                                                <div key={orderKey} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            <div className="flex items-center space-x-2 mb-2">
                                                                <h4 className="font-medium text-gray-900">
                                                                    {getEntityTranslation(diningOptionObjects[order.diningOption], language, translationDetails) || translateDynamic(order.diningOption, t)} {order.tableNumber}
                                                                </h4>
                                                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                                                    {order.products.length} {t('items')}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-600">
                                                                {t('total_label')} {currency}{(parseFloat(order.total) || 0).toFixed(2)}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mb-2">
                                                                {t('staff_label')} {getDisplayName(username, employeeName) || order.cashier || t('unknown')}
                                                            </p>
                                                            <div className="text-sm text-gray-500 bg-gray-50 rounded p-2">
                                                                <div className="font-medium text-xs text-gray-700 mb-1">{t('items_colon')}</div>
                                                                {order.products.slice(0, 2).map((product, idx) => (
                                                                    <div key={idx} className="text-xs">
                                                                        {product.quantity}x {getProductName(product, language, translationDetails) ?? getTransliteration(product.name, language, t)}
                                                                    </div>
                                                                ))}
                                                                {order.products.length > 2 && (
                                                                    <div className="text-xs text-gray-400 mt-1">
                                                                        + {order.products.length - 2} more items
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col space-y-2 ml-4">
                                                            <button
                                                                onClick={() => startEditOrder(orderKey)}
                                                                className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
                                                            >
                                                                {t('edit_order')}
                                                            </button>
                                                            <button
                                                                onClick={() => handleChangeTable(orderKey)}
                                                                className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 transition-colors"
                                                            >
                                                                {t('move_table')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Product Selection */}
                        {currentStep === 2 && (
                            <div className="p-4 space-y-6">
                                {/* Selected Table Info */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <p className="text-blue-800 font-medium">
                                        {getEntityTranslation(diningOptionObjects[selectedDiningOption], language, translationDetails) || translateDynamic(selectedDiningOption, t)} {tableNumber}
                                    </p>
                                </div>

                                {/* Categories */}
                                <div>
                                    <h2 className="text-lg font-medium text-gray-900 mb-4">
                                        {t('select_category')}
                                    </h2>
                                    <div className="grid grid-cols-2 gap-3">
                                        {categories.map(category => (
                                            <SelectableCard
                                                key={category}
                                                label={getEntityTranslation(categoryObjects[category], language, translationDetails) || translateDynamic(category, t)}
                                                selected={selectedCategory === category}
                                                icon={getCategoryIcon(categoryIcons[category] || 'star', { size: 40, color: selectedCategory === category ? '#2563eb' : '#94a3b8' })}
                                                layout="icon-top"
                                                onClick={() => {
                                                    // Toggle category on/off
                                                    if (selectedCategory === category) {
                                                        setSelectedCategory(null); // Unselect if same category clicked
                                                    } else {
                                                        setSelectedCategory(category); // Select new category
                                                    }
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                                {/* Products Section with Subcategories */}
                                {selectedCategory && (
                                    <div>
                                        {/* Subcategories (if available) */}
                                        {subcategories.length > 0 && (
                                            <div className="mb-6">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="text-md font-medium text-gray-900">
                                                        {t('select_subcategory')}
                                                    </h3>
                                                    {selectedSubcategory && (
                                                        <button
                                                            onClick={() => setSelectedSubcategory(null)}
                                                            className="text-sm text-primary-600 hover:text-primary-800"
                                                        >
                                                            {t('show_all')}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 mb-4">
                                                    {subcategories.map(subcat => (
                                                        <SelectableCard
                                                            key={subcat.id}
                                                            label={getEntityTranslation(subcat, language, translationDetails) || getTransliteration(subcat.name, language, t)}
                                                            selected={selectedSubcategory === subcat.name}
                                                            onClick={() => {
                                                                // Toggle subcategory on/off
                                                                if (selectedSubcategory === subcat.name) {
                                                                    setSelectedSubcategory(null);
                                                                } else {
                                                                    setSelectedSubcategory(subcat.name);
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mb-6">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder={t('search_products_placeholder')}
                                                    value={productSearch}
                                                    onChange={(e) => setProductSearch(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-base"
                                                />
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                    </svg>
                                                </div>
                                                {productSearch && (
                                                    <button
                                                        onClick={() => setProductSearch('')}
                                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                    >
                                                        <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                            {productSearch && (
                                                <p className="text-sm text-gray-600 mt-2 text-center">
                                                    {t('search_results_text', { count: filteredProducts.length, plural: filteredProducts.length !== 1 ? 's' : '', query: productSearch })}
                                                </p>
                                            )}
                                        </div>

                                        {/* Products */}
                                        <div>
                                            <h3 className="text-md font-medium text-gray-900 mb-4">
                                                {t('select_products')}
                                                {selectedSubcategory && (
                                                    <span className="text-sm text-gray-500 ml-2">
                                                        ({(() => { const sc = subcategories.find(s => s.name === selectedSubcategory); return (sc && getEntityTranslation(sc, language, translationDetails)) || translateDynamic(selectedSubcategory, t); })()})
                                                    </span>
                                                )}
                                            </h3>
                                            <div className="grid grid-cols-1 gap-3">
                                                {filteredProducts.length > 0 ? (
                                                    filteredProducts.map(product => {
                                                        let displayStock;
                                                        let isOutOfStock;

                                                        if (product.stock_calculation_type === 'auto') {
                                                            const stockData = bulkCalculatedStock[product.product_code];
                                                            const calculatedStock = stockData ? stockData.available_stock : 0;
                                                            displayStock = stockData ? calculatedStock : '...';
                                                            isOutOfStock = calculatedStock < 1;
                                                        } else {
                                                            displayStock = product.quantity === -9999 ? "∞" : product.quantity;
                                                            isOutOfStock = product.quantity < 1 && product.quantity !== -9999;
                                                        }

                                                        return (
                                                            <div
                                                                key={product.product_code}
                                                                className={`p-4 border rounded-lg ${isOutOfStock ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-200 hover:border-primary-500'}`}
                                                                onClick={() => !isOutOfStock && handleProductSelect(product)}
                                                            >
                                                                <div className="flex justify-between items-start">
                                                                    <div>
                                                                        <h4 className="font-medium text-gray-900">
                                                                            {getProductName(product, language, translationDetails) ?? getTransliteration(product.name, language, t)}
                                                                        </h4>
                                                                        <p className="text-sm text-gray-600">
                                                                            {currency}{product.selling_price}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500">
                                                                            {t('stock_label')}: {displayStock}
                                                                        </p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        {isOutOfStock ? (
                                                                            <span className="text-red-600 text-sm">{t('out_of_stock')}</span>
                                                                        ) : (
                                                                            <button className="bg-primary-600 text-white px-3 py-1 rounded text-sm">
                                                                                {t('add')}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="text-center py-8 text-gray-500">
                                                        <p className="text-sm">
                                                            {selectedSubcategory
                                                                ? t('no_products_found_in_subcategory', {
                                                                    subcategory: translateDynamic(selectedSubcategory, language, t)
                                                                })
                                                                : t('no_products_found_in_category', {
                                                                    category: translateDynamic(selectedCategory, t)
                                                                })
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Current Order Summary */}
                                {selectedProducts.length > 0 && (
                                    <div className="bg-white border rounded-lg p-4">
                                        <h3 className="font-medium text-gray-900 mb-3">{t('current_order')}</h3>
                                        <div className="space-y-3">
                                            {selectedProducts.map((product, index) => {
                                                const itemKey = product.uniqueId || `${product.product_code}-${index}`;

                                                // Calculate individual product total including add-ons
                                                const basePrice = (parseFloat(product.selling_price) || 0) * (parseInt(product.quantity) || 1);
                                                const addonPrice = product.add_ons
                                                    ? product.add_ons.reduce((sum, addon) => sum + ((parseFloat(addon.price) || 0) * (parseInt(product.quantity) || 1)), 0)
                                                    : 0;
                                                const totalPrice = basePrice + addonPrice;

                                                return (
                                                    <div key={itemKey} className="border-b border-gray-100 pb-2 last:border-b-0">
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="font-medium">
                                                                        {product.quantity}x {product.name}
                                                                    </span>
                                                                    <div className="flex items-center space-x-2">
                                                                        <span>{currency}{totalPrice.toFixed(2)}</span>
                                                                        <button
                                                                            onClick={() => removeProductFromOrder(product.uniqueId || product.product_code)}
                                                                            className="text-red-600 p-1"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {/* Show add-ons under the item */}
                                                                {product.add_ons && product.add_ons.length > 0 && (
                                                                    <div className="mt-1 ml-4 space-y-1">
                                                                        {product.add_ons.map((addon, idx) => (
                                                                            <div key={idx} className="text-xs text-gray-600 flex justify-between">
                                                                                <span>+ {getProductName(addon, language, translationDetails) ?? getTransliteration(addon.name, language, t)}</span>
                                                                                <span>+{currency}{(parseFloat(addon.price) * product.quantity).toFixed(2)}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {/* Show notes if any */}
                                                                {product.notes && (
                                                                    <p className="text-xs text-gray-500 mt-1 ml-4 italic">
                                                                        {t('note_text', { note: product.notes })}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div className="border-t pt-2 mt-2">
                                                <div className="flex justify-between">
                                                    <span>{t('subtotal')}</span>
                                                    <span>{currency}{calculateTotal().subtotal.toFixed(2)}</span>
                                                </div>
                                                {addServiceCharge && (
                                                    <div className="flex justify-between text-sm">
                                                        <span>{t('service_charge', { percent: Math.round((tenantConfig?.service_charge_rate || 0.12) * 100) })}</span>
                                                        <span>{currency}{calculateTotal().serviceCharge.toFixed(2)}</span>
                                                    </div>
                                                )}
                                                {addTax && calculateTotal().tax > 0 && (
                                                    <div className="flex justify-between text-sm">
                                                        <span>{t('tax_with_percent', { percent: Math.round((tenantConfig?.tax_rate || 0) * 100) })}</span>
                                                        <span>{currency}{calculateTotal().tax.toFixed(2)}</span>
                                                    </div>
                                                )}
                                                {/* Promo discount line */}
                                                {appliedPromo && (
                                                    <div className="flex items-center justify-between text-green-700 bg-green-50 rounded px-2 py-1 text-sm">
                                                        <span className="flex items-center gap-1">
                                                            🏷️ <span className="font-medium">{appliedPromo.promotion.name}</span>
                                                            <button onClick={removePromo} className="ml-1 text-gray-400 hover:text-red-500 text-xs">✕</button>
                                                        </span>
                                                        <span className="font-semibold">−{currency}{promoDiscountAmount.toFixed(2)}</span>
                                                    </div>
                                                )}
                                                {/* Apply offer button — shows when any eligible promo exists */}
                                                {promoAllEligible.length > 0 && (
                                                    <button
                                                        onClick={() => setShowPromoModal(true)}
                                                        className="w-full mt-1 py-1.5 rounded text-xs font-medium text-white"
                                                        style={{ backgroundColor: '#059669' }}
                                                    >
                                                        {t('apply_offer')}
                                                    </button>
                                                )}
                                                <div className="flex justify-between font-medium">
                                                    <span>{t('total')}</span>
                                                    <span>{currency}{Math.max(0, calculateTotal().total - promoDiscountAmount).toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Continue Button */}
                                {selectedProducts.length > 0 && (
                                    <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
                                        <button
                                            onClick={() => setCurrentStep(3)}
                                            className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium"
                                        >
                                            {t('continue_to_confirm', { count: selectedProducts.length })}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 3: Confirm Order */}
                        {currentStep === 3 && (
                            <div className="p-4 space-y-6 pb-24">
                                {/* Order Details */}
                                <div className="bg-white border rounded-lg p-4">
                                    <h2 className="text-lg font-medium text-gray-900 mb-4">{t('order_summary')}</h2>

                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span>{t('table_colon')}</span>
                                            <span className="font-medium">{getEntityTranslation(diningOptionObjects[selectedDiningOption], language, translationDetails) || translateDynamic(selectedDiningOption, t)} {tableNumber}</span>
                                        </div>

                                        <div className="border-t pt-3">
                                            <h3 className="font-medium mb-3">{t('items_colon')}</h3>
                                            <div className="space-y-3">
                                                {selectedProducts.map((product, productIndex) => {
                                                    const isOriginal = isOriginalProduct(product);
                                                    const originalQty = getOriginalQuantity(product);
                                                    const isNewItem = !isOriginal;
                                                    const isAddedQuantity = isOriginal && product.quantity > originalQty;

                                                    // Calculate individual product total including add-ons
                                                    const basePrice = (parseFloat(product.selling_price) || 0) * (parseInt(product.quantity) || 1);
                                                    const addonPrice = product.add_ons
                                                        ? product.add_ons.reduce((sum, addon) => sum + ((parseFloat(addon.price) || 0) * (parseInt(product.quantity) || 1)), 0)
                                                        : 0;
                                                    const totalPrice = basePrice + addonPrice;

                                                    return (
                                                        <div key={product.uniqueId || product.product_code}
                                                            className={`border rounded-lg p-3 ${isNewItem ? 'border-green-200 bg-green-50' :
                                                                    isAddedQuantity ? 'border-blue-200 bg-blue-50' :
                                                                        'border-gray-200 bg-gray-50'
                                                                }`}>

                                                            {/* Product Header */}
                                                            <div className="flex justify-between items-start mb-2">
                                                                <div className="flex-1">
                                                                    <div className="flex items-center space-x-2">
                                                                        <span className="font-medium">{getProductName(product, language, translationDetails) ?? getTransliteration(product.name, language, t)}</span>
                                                                        {isNewItem && (
                                                                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                                                                {t('new')}
                                                                            </span>
                                                                        )}
                                                                        {isAddedQuantity && (
                                                                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                                                                +{product.quantity - originalQty} {t('added')}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    {/* Show add-ons */}
                                                                    {product.add_ons && product.add_ons.length > 0 && (
                                                                        <div className="mt-1 ml-2">
                                                                            {product.add_ons.map((addon, idx) => (
                                                                                <div key={idx} className="text-xs text-gray-600">
                                                                                    + {getProductName(addon, language, translationDetails) ?? getTransliteration(addon.name, language, t)} (+{currency}{(parseFloat(addon.price) * product.quantity).toFixed(2)})
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    {/* Show notes */}
                                                                    {product.notes && (
                                                                        <p className="text-xs text-gray-500 mt-1 italic">{t('note_text', { note: product.notes })}</p>
                                                                    )}
                                                                </div>
                                                                <span className="font-medium">{currency}{totalPrice.toFixed(2)}</span>
                                                            </div>

                                                            {/* Quantity Controls */}
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <button
                                                                        onClick={() => {
                                                                            const newQty = Math.max(1, product.quantity - 1);
                                                                            updateProductQuantity(productIndex, newQty);
                                                                        }}
                                                                        disabled={isOriginal && product.quantity <= originalQty}
                                                                        className={`w-8 h-8 rounded-full border ${isOriginal && product.quantity <= originalQty
                                                                                ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                                                                                : 'border-gray-400 text-gray-600 hover:bg-gray-100'
                                                                            } flex items-center justify-center`}
                                                                    >
                                                                        -
                                                                    </button>

                                                                    <span className="font-medium text-lg min-w-[2rem] text-center">
                                                                        {product.quantity}
                                                                    </span>

                                                                    <button
                                                                        onClick={() => {
                                                                            updateProductQuantity(productIndex, product.quantity + 1);
                                                                        }}
                                                                        className="w-8 h-8 rounded-full border border-gray-400 text-gray-600 hover:bg-gray-100 flex items-center justify-center"
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>

                                                                {/* Remove Button - Only for new items or complete removal of original items */}
                                                                {/* Remove Button - Only for new items */}
                                                                {isNewItem && (
                                                                    <button
                                                                        onClick={() => {
                                                                            const updatedProducts = selectedProducts.filter((_, index) => index !== productIndex);
                                                                            setSelectedProducts(updatedProducts);
                                                                        }}
                                                                        className="text-red-600 hover:text-red-800 px-3 py-1 rounded text-sm bg-red-100"
                                                                    >
                                                                        {t('remove')}
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {/* Original quantity indicator for modified items */}
                                                            {isOriginal && product.quantity !== originalQty && (
                                                                <div className="mt-2 text-xs text-gray-500 bg-white rounded px-2 py-1">
                                                                    {t('original_quantity')}: {originalQty} | {t('current_quantity')}: {product.quantity}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="border-t pt-3">
                                            <div className="flex justify-between">
                                                <span>{t('subtotal')}</span>
                                                <span>{currency}{calculateTotal().subtotal.toFixed(2)}</span>
                                            </div>
                                            {addServiceCharge && (
                                                <div className="flex justify-between">
                                                    <span>{t('service_charge', { percent: Math.round((tenantConfig?.service_charge_rate || 0.12) * 100) })}</span>
                                                    <span>{currency}{calculateTotal().serviceCharge.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {addTax && calculateTotal().tax > 0 && (
                                                <div className="flex justify-between">
                                                    <span>{t('tax_with_percent', { percent: Math.round((tenantConfig?.tax_rate || 0) * 100) })}</span>
                                                    <span>{currency}{calculateTotal().tax.toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between font-medium text-lg">
                                                <span>{t('total')}</span>
                                                <span>{currency}{calculateTotal().total.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Legend for editing mode */}
                                {editingOrderKey && (
                                    <div className="bg-white border rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900 mb-2">{t('editing_guide')}</h4>
                                        <div className="space-y-1 text-sm">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
                                                <span>{t('new_items_full_control')}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded"></div>
                                                <span>{t('existing_items_can_only_add_more')}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded"></div>
                                                <span>{t('existing_items_original_quantity')}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notes */}
                                <div className="bg-white border rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('special_notes_optional')}
                                    </label>
                                    <textarea
                                        rows={3}
                                        className="w-full border border-gray-300 rounded-md p-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder={t('any_special_requests_placeholder')}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>

                                {/* Submit Button */}
                                <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
                                    {editingOrderKey ? (
                                        <div className="space-y-2">
                                            <button
                                                onClick={saveEditedOrder}
                                                disabled={loading}
                                                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium disabled:bg-gray-400"
                                            >
                                                {loading ? t('updating') : t('update_order')}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setEditingOrderKey(null);
                                                    setSelectedProducts([]);
                                                    setNotes('');
                                                    setCurrentStep(1);
                                                    setOriginalProductsBeforeEdit([]);
                                                }}
                                                className="w-full bg-gray-400 text-white py-2 rounded-lg font-medium"
                                            >
                                                {t('cancel_edit')}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={submitOrder}
                                            disabled={loading}
                                            className="w-full bg-green-600 text-white py-3 rounded-lg font-medium disabled:bg-gray-400"
                                        >
                                            {loading ? t('submitting') : t('submit_order')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}


                </>
            )}

                {/* Product Modal */}
                {showProductModal && selectedProduct && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[80vh] overflow-y-auto">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                {getProductName(selectedProduct, language, translationDetails) ?? getTransliteration(selectedProduct.name, language, t)}
                            </h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('quantity')}
                                    </label>
                                    <div className="flex items-center space-x-3">
                                        <button
                                            onClick={() => setProductQuantity(Math.max(1, productQuantity - 1))}
                                            className="p-2 border rounded-md hover:bg-gray-50"
                                        >
                                            -
                                        </button>
                                        <span className="px-4 py-2 border rounded-md text-center min-w-16">
                                            {productQuantity}
                                        </span>
                                        <button
                                            onClick={() => {
                                                // Calculate max available stock
                                                let maxStock;
                                                if (selectedProduct.stock_calculation_type === 'auto') {
                                                    const stockData = bulkCalculatedStock[selectedProduct.product_code];
                                                    maxStock = stockData ? stockData.available_stock : 0;
                                                } else {
                                                    maxStock = selectedProduct.quantity === -9999 ? 999 : selectedProduct.quantity;
                                                }

                                                // Only increase if under limit
                                                if (productQuantity < maxStock) {
                                                    setProductQuantity(productQuantity + 1);
                                                } else {
                                                    setError(t('only_x_available_in_stock', { count: maxStock, product: selectedProduct.name }));
                                                    setTimeout(() => setError(''), 3000);
                                                }
                                            }}
                                            className="p-2 border rounded-md hover:bg-gray-50"
                                        >
                                            +
                                        </button>
                                    </div>
                                    {(() => {
                                        // Show stock info
                                        let stockInfo;
                                        if (selectedProduct.stock_calculation_type === 'auto') {
                                            const stockData = bulkCalculatedStock[selectedProduct.product_code];
                                            stockInfo = stockData ? stockData.available_stock : 0;
                                        } else {
                                            stockInfo = selectedProduct.quantity === -9999 ? "∞" : selectedProduct.quantity;
                                        }
                                        return (
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t('available_stock', { stock: stockInfo })}
                                            </p>
                                        );
                                    })()}
                                </div>

                                {/* Add-ons Section */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('add_ons')}
                                    </label>
                                    {selectedProduct.available_addons && selectedProduct.available_addons.length > 0 ? (
                                        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                                            {selectedProduct.available_addons.map(addon => (
                                                <div key={addon.product_code} className="flex items-center justify-between p-2 border rounded hover:bg-gray-50">
                                                    <div>
                                                        <div className="font-medium text-sm">{getProductName(addon, language, translationDetails) ?? getTransliteration(addon.name, language, t)}</div>
                                                        <div className="text-xs text-gray-500">+{currency}{parseFloat(addon.price).toFixed(2)}</div>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedAddons.includes(addon.product_code)}
                                                        onChange={() => {
                                                            if (selectedAddons.includes(addon.product_code)) {
                                                                setSelectedAddons(prev => prev.filter(code => code !== addon.product_code));
                                                            } else {
                                                                setSelectedAddons(prev => [...prev, addon.product_code]);
                                                            }
                                                        }}
                                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-500 italic border border-gray-200 rounded-md p-3 bg-gray-50">
                                            {t('no_add_ons_for_product')}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('notes_optional')}
                                    </label>
                                    <textarea
                                        rows={2}
                                        className="w-full border border-gray-300 rounded-md p-2"
                                        placeholder={t('special_requests_placeholder')}
                                        value={productNotes}
                                        onChange={(e) => setProductNotes(e.target.value)}
                                    />
                                </div>

                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>{t('price_each')}</span>
                                    <span>{currency}{selectedProduct.selling_price}</span>
                                </div>

                                {selectedAddons.length > 0 && (
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span>{t('add_ons_total')}</span>
                                        <span>
                                            {currency}{selectedAddons.reduce((total, addonCode) => {
                                                const addon = selectedProduct.available_addons.find(a => a.product_code === addonCode);
                                                return total + (addon ? parseFloat(addon.price) : 0);
                                            }, 0).toFixed(2)}
                                        </span>
                                    </div>
                                )}

                                <div className="flex justify-between font-medium">
                                    <span>{t('total')}</span>
                                    <span>
                                        {currency}{((parseFloat(selectedProduct.selling_price) +
                                            selectedAddons.reduce((total, addonCode) => {
                                                const addon = selectedProduct.available_addons.find(a => a.product_code === addonCode);
                                                return total + (addon ? parseFloat(addon.price) : 0);
                                            }, 0)) * productQuantity).toFixed(2)}
                                    </span>
                                </div>
                            </div>

                            <div className="flex space-x-3 mt-6">
                                <button
                                    onClick={() => setShowProductModal(false)}
                                    className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-50"
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    onClick={addProductToOrder}
                                    className="flex-1 bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700"
                                >
                                    {t('add_to_order')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Change Table Modal */}
                <ChangeTableModal
                    isOpen={changeTableModalOpen}
                    onClose={() => setChangeTableModalOpen(false)}
                    orderToChangeTable={orderToChangeTable}
                    pendingOrders={pendingOrders}
                    diningOptions={diningOptions}
                    availableTableNumbers={availableTableNumbers}
                    newDiningOption={newDiningOption}
                    setNewDiningOption={setNewDiningOption}
                    newTableNumber={newTableNumber}
                    setNewTableNumber={setNewTableNumber}
                    onConfirm={processTableChange}
                    confirmDisabled={!newTableNumber || !newDiningOption || loading}
                    confirmLabel={loading ? t('changing') : t('change_table')}
                />

                {/* ── Promo picker modal ──────────────────────────── */}
                {showPromoModal && (
                    <div className="fixed inset-0 z-50 flex items-end justify-center">
                        <div
                            onClick={() => setShowPromoModal(false)}
                            className="absolute inset-0 bg-black bg-opacity-30"
                        />
                        <div className="relative bg-white rounded-t-2xl w-full max-w-md p-4 shadow-xl">
                            <h3 className="text-base font-bold mb-3 text-gray-900">{t('select_offer')}</h3>
                            {promoAllEligible.length === 0 ? (
                                <p className="text-sm text-gray-500 py-4 text-center">{t('no_offers_available')}</p>
                            ) : (
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {promoAllEligible.map(({ promotion, discount }) => (
                                        <button
                                            key={promotion.promotion_id}
                                            onClick={() => selectPromo({ promotion, discount })}
                                            className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                                appliedPromo?.promotion?.promotion_id === promotion.promotion_id
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                                            }`}
                                        >
                                            <div className="flex justify-between items-center">
                                                <span className="font-medium text-sm text-gray-900">{promotion.name}</span>
                                                <span className="text-green-600 text-sm font-bold">−{currency}{discount.toFixed(2)}</span>
                                            </div>
                                            {promotion.description && (
                                                <p className="text-xs text-gray-500 mt-1">{promotion.description}</p>
                                            )}
                                            <p className="text-xs text-green-700 mt-0.5">{t('saving')}: {currency}{discount.toFixed(2)}</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                            <button
                                onClick={() => setShowPromoModal(false)}
                                className="mt-3 w-full py-2 rounded-lg border border-gray-300 text-sm text-gray-700"
                            >
                                {t('cancel')}
                            </button>
                        </div>
                    </div>
                )}
        </div>
    );
};

export default WaiterSalesPage;